import React, { useState, useEffect, useContext, useRef } from "react";
import { DataContext } from "../context/DataContext";
import {
  Text,
  Button,
  Stack,
  Box,
  Loader,
  useMantineTheme,
} from "@mantine/core";
import { IconCameraOff, IconExternalLink, IconCopy, IconCheck, IconRefresh } from "@tabler/icons-react";
import useLocale from "../hooks/useLocale";
import useStatusCheck from "../hooks/useStatusCheck";
import useEdgeStatusCheck from "../hooks/useEdgeStatusCheck";
import { trackEvent } from "../utils/telemetry";

export default function BrowserHandoff({ nextPath, onTryAgain }) {
  const { settings, dispatch, data, postPayloadMessage } = useContext(DataContext);
  const theme = useMantineTheme();
  const locale = useLocale("document-capture");
  const [url, setUrl] = useState("");
  const [opened, setOpened] = useState(false);
  const [qrFound, setQrFound] = useState(false);
  const [isTimeUp, setIsTimeUp] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [hasNotifiedHandoff, setHasNotifiedHandoff] = useState(false);
  const shownFiredRef = useRef(false);

  const isEdgeMode = data?.edge === true;

  // Track handoff shown event
  useEffect(() => {
    if (shownFiredRef.current) return;
    shownFiredRef.current = true;
    trackEvent("id_doc_browser_handoff_shown");
  }, []);

  // Generate handoff URL (same pattern as handoff/index.js)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const frontendParam = urlParams.get("frontend");
    const edgeParam = urlParams.get("edge");

    urlParams.set("qr", "true");
    urlParams.set("browser_handoff", "true");
    // Mobile handoff receiver has already seen the verification
    // notice on desktop — carry a flag so App.js can bypass it.
    urlParams.set("notice_shown", "true");
    if (nextPath) {
      urlParams.set("nextPath", nextPath);
    }
    if (edgeParam) {
      urlParams.set("edge", edgeParam);
    }
    if (data?.selected_test_case_id) {
      urlParams.set("test_case_id", data.selected_test_case_id);
    }
    const queryString = urlParams.toString();

    const hostname = window.location.hostname;
    const isLocalhost =
      hostname === "localhost" ||
      hostname.startsWith("127.0.0.") ||
      hostname.startsWith("0.0.0.0");

    if (isLocalhost) {
      setUrl(`${window.location.origin}/${settings.request_id}?${queryString}`);
    } else if (hostname.endsWith(".pages.dev")) {
      setUrl(`${window.location.origin}/${settings.request_id}?${queryString}`);
    } else {
      const useVxFrontend = hostname === "vx-fe.dcams.app" || frontendParam === "vx-fe";
      let urlToUse;
      if (useVxFrontend) {
        urlToUse = `https://vx-fe.dcams.app/${settings.request_id}?${queryString}`;
      } else if (hostname.endsWith("dcams.app")) {
        urlToUse = `${window.location.origin}/${settings.request_id}?${queryString}`;
      } else {
        urlToUse = `${process.env.REACT_APP_FRONTEND_URL}/${settings.request_id}?${queryString}`;
      }
      setUrl(urlToUse);
    }
  }, [settings, nextPath, data?.selected_test_case_id]);

  // Edge mode status polling
  const handleEdgeStatus = (result) => {
    const { process_state: processState, status } = result || {};

    // Same guard as the dedicated Handoff page: ignore residual
    // terminal status from a prior journey step until this step's
    // own handoff completes. Without it, polling immediately after
    // mount picks up the previous step's FAIL/PASS and short-
    // circuits this step before the QR is ever scanned.
    if (!qrFound) {
      if (processState === "HANDED_OFF") {
        setQrFound(true);
      }
      return true;
    }

    if (processState === "COMPLETED" || status === "PASS" || status === "FAIL") {
      trackEvent("id_doc_browser_handoff_completed", {
        status: status === "PASS" ? "PASS" : "FAIL",
      });
      dispatch({ type: "SET_STATUS", payload: status === "PASS" ? "PASS" : "FAIL" });
      dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
      return false;
    }
    return true;
  };

  // Non-edge status handler — fire completion event when PASS/FAIL detected
  const handleStatusResult = (result) => {
    if (result?.status === "PASS" || result?.status === "FAIL") {
      trackEvent("id_doc_browser_handoff_completed", {
        status: result.status,
      });
    }
    // Return undefined to let useStatusCheck handle dispatching normally
  };

  useEdgeStatusCheck(isEdgeMode && opened, handleEdgeStatus, true);
  useStatusCheck(!isEdgeMode && qrFound, true, "handoff", handleStatusResult);

  // QR status polling (non-edge mode)
  useEffect(() => {
    if (isEdgeMode || !opened || qrFound) return;

    const fetchQrStatus = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/v3/stable/api/frontend/qrStatus`,
          {
            method: "POST",
            body: JSON.stringify({
              request_id: settings.request_id,
              type: "get",
              site_name: settings.site_name,
            }),
          }
        );
        const result = await response.json();
        if (result.found) {
          setQrFound(true);
        }
      } catch (error) {
        console.error("[BrowserHandoff] Error fetching QR status:", error);
      }
    };

    const intervalId = setInterval(fetchQrStatus, 6000);
    return () => clearInterval(intervalId);
  }, [opened, qrFound, settings, isEdgeMode]);

  // Send postMessage and track event when browser session is detected
  useEffect(() => {
    if (qrFound && postPayloadMessage && !hasNotifiedHandoff) {
      setHasNotifiedHandoff(true);
      postPayloadMessage({
        event: "USER_MOVED_TO_MOBILE",
        app: nextPath || "handoff",
      });
      trackEvent("id_doc_browser_handoff_started");
    }
  }, [qrFound, postPayloadMessage, nextPath, hasNotifiedHandoff]);

  // 5-minute timeout
  useEffect(() => {
    if (!opened) return;
    const timerId = setTimeout(() => setIsTimeUp(true), 5 * 60 * 1000);
    return () => clearTimeout(timerId);
  }, [opened]);

  const handleOpenBrowser = () => {
    trackEvent("id_doc_browser_handoff_open_clicked");
    setOpened(true);
    window.open(url, "_system") || window.open(url, "_blank");
  };

  const handleCopyLink = async () => {
    trackEvent("id_doc_browser_handoff_copy_clicked");
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      // Fallback: select a hidden input
      const input = document.createElement("input");
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  const handleTryAgain = () => {
    trackEvent("id_doc_browser_handoff_try_again");
    if (onTryAgain) onTryAgain();
  };

  const restartFetch = () => {
    setIsTimeUp(false);
    setQrFound(false);
    setOpened(false);
  };

  const resolvedPrimaryColor = data?.styles?.primaryColor ?? theme.primaryColor;

  // Waiting state — user has opened the browser
  if (opened) {
    return (
      <Stack
        align="center"
        justify="center"
        style={{
          height: "100%",
          minHeight: "400px",
          padding: "40px 20px",
        }}
      >
        <Loader size="lg" />
        <Stack spacing={4} align="center" mt="md">
          <Text fz="xl" fw={700} align="center">
            {locale?.browserHandoff?.waitingTitle || "Verification in Progress"}
          </Text>
          <Text c="dimmed" align="center" fz="sm" maw={320}>
            {locale?.browserHandoff?.waitingSubtitle || "Complete the verification in your browser. This page will update automatically."}
          </Text>
        </Stack>
        {isTimeUp && (
          <Button
            variant="outline"
            onClick={restartFetch}
            mt="lg"
            leftIcon={<IconRefresh size={16} />}
          >
            {locale?.browserHandoff?.refreshStatus || "Refresh Status"}
          </Button>
        )}
      </Stack>
    );
  }

  // Initial state — show open-in-browser options
  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        minHeight: "400px",
        padding: "24px 20px",
      }}
    >
      <IconCameraOff size={64} color={theme.colors.gray[5]} stroke={1.5} />
      <Text fz={24} fw={700} align="center" mt="lg">
        {locale?.browserHandoff?.title || "Camera Not Available"}
      </Text>
      <Text c="dimmed" fz={14} align="center" mt="sm" maw={340}>
        {locale?.browserHandoff?.subtitle || "Your browser doesn't support camera access. Continue in your device's default browser to complete verification."}
      </Text>

      <Stack spacing="sm" mt="xl" style={{ width: "100%", maxWidth: 320 }}>
        <Button
          size="lg"
          fullWidth
          onClick={handleOpenBrowser}
          leftIcon={<IconExternalLink size={20} />}
          color={resolvedPrimaryColor}
        >
          {locale?.browserHandoff?.openBrowser || "Open in Browser"}
        </Button>
        <Button
          variant="light"
          size="md"
          fullWidth
          onClick={handleCopyLink}
          leftIcon={linkCopied ? <IconCheck size={18} /> : <IconCopy size={18} />}
          color={linkCopied ? "teal" : resolvedPrimaryColor}
        >
          {linkCopied
            ? (locale?.browserHandoff?.linkCopied || "Link copied!")
            : (locale?.browserHandoff?.copyLink || "Copy Link")}
        </Button>
        <Button
          variant="subtle"
          size="md"
          fullWidth
          color="gray"
          onClick={handleTryAgain}
        >
          {locale?.browserHandoff?.tryAgain || "Try Again on This Page"}
        </Button>
      </Stack>
    </Box>
  );
}
